import { Directive, Input, TemplateRef, OnInit, OnDestroy, ElementRef, HostListener, ViewContainerRef } from '@angular/core';
import { ComponentType, ComponentPortal, TemplatePortal } from '@angular/cdk/portal';
import { OverlayRef, Overlay, OverlayPositionBuilder } from '@angular/cdk/overlay';

// How long a tapped tooltip stays up on touch devices before dismissing itself.
const TOUCH_VISIBLE_MS = 4000;
// A tap fires touchstart -> ... -> click. Ignore the trailing click for this
// long so it can't undo what the tap just did.
const TOUCH_CLICK_GRACE_MS = 600;

@Directive({
    selector: '[customTooltipOver]',
    standalone: false
})
export class CustomTooltipOverDirective implements OnInit, OnDestroy {

  @Input('customTooltipOver') tooltipContent!: TemplateRef<any> | ComponentType<any>;

  private _overlayRef!: OverlayRef;
  private showTimeout!: any;
  private hideTimeout!: any;
  private isTooltipVisible: boolean = false; // Track visibility state
  private lastTouchTime = 0;

  constructor(
    private overlay: Overlay,
    private overlayPositionBuilder: OverlayPositionBuilder,
    private elementRef: ElementRef,
    private viewContainerRef: ViewContainerRef,
  ) { }

  ngOnInit(): void {
    if (this.tooltipContent) {
      const position = this.overlayPositionBuilder
        .flexibleConnectedTo(this.elementRef)
        .withPositions([
          {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
            offsetX: 0,
            offsetY: 8,
          },
          {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom',
            offsetX: 0,
            offsetY: -8,
          }
        ]);

      this._overlayRef = this.overlay.create({
        positionStrategy: position,
        scrollStrategy: this.overlay.scrollStrategies.close(),
        panelClass: 'custom-tooltip-over',
      });
    } else {
      console.error('[ERROR] Tooltip content is required.');
    }
  }

  private _attachContent(): void {
    if (!this._overlayRef.hasAttached()) {
      let containerPortal: TemplatePortal<any> | ComponentPortal<any>;

      if (this.tooltipContent instanceof TemplateRef) {
        containerPortal = new TemplatePortal(this.tooltipContent, this.viewContainerRef);
      } else {
        containerPortal = new ComponentPortal(this.tooltipContent, this.viewContainerRef);
      }

      this._overlayRef.attach(containerPortal);
      this.isTooltipVisible = true; // Update state
    }
  }

  private _detachContent(): void {
    if (this._overlayRef.hasAttached()) {
      this._overlayRef.detach();
      this.isTooltipVisible = false; // Update state
    }
  }

  @HostListener('mouseenter')
  _show(): void {
    clearTimeout(this.hideTimeout); // Cancel any pending hide operation

    // Add a small delay before showing to handle fast mouse movements
    this.showTimeout = setTimeout(() => {
      if (!this.isTooltipVisible) {
        this._attachContent(); // Attach content if not visible
      }
    }, 100); // Small delay before showing tooltip (adjust as needed)
  }

  @HostListener('mouseleave')
  _hide(): void {
    clearTimeout(this.showTimeout); // Cancel any pending show operation

    // Add a small delay before hiding to handle fast mouse movements
    this.hideTimeout = setTimeout(() => {
      if (this.isTooltipVisible) {
        this._detachContent(); // Detach content if visible
      }
    }, 100); // Small delay before hiding tooltip (adjust as needed)
  }

  // Touch devices (iPad) never fire mouseenter/mouseleave on a plain icon, so
  // tap toggles the tooltip instead of hovering it.
  @HostListener('touchstart')
  _onTouchStart(): void {
    this.lastTouchTime = Date.now();
    clearTimeout(this.showTimeout);
    clearTimeout(this.hideTimeout);

    if (this.isTooltipVisible) {
      this._detachContent(); // Tapping the icon again closes it
      return;
    }

    this._attachContent();
    this.hideTimeout = setTimeout(() => this._detachContent(), TOUCH_VISIBLE_MS);
  }

  // Tapping anywhere else dismisses it, standing in for mouseleave.
  @HostListener('document:touchstart', ['$event'])
  _onDocumentTouchStart(event: Event): void {
    if (this.elementRef.nativeElement.contains(event.target as Node)) return;

    clearTimeout(this.showTimeout);
    clearTimeout(this.hideTimeout);
    this._detachContent();
  }

  @HostListener('click')
  _forceHide(): void {
    // On touch, the tap already decided whether to show or hide — don't let the
    // synthesized click that follows it cancel a tooltip that just opened.
    if (Date.now() - this.lastTouchTime < TOUCH_CLICK_GRACE_MS) return;

    // Ensure immediate hide on click
    clearTimeout(this.showTimeout);
    clearTimeout(this.hideTimeout);
    this._detachContent(); // Detach content on click
  }

  ngOnDestroy(): void {
    clearTimeout(this.showTimeout);
    clearTimeout(this.hideTimeout);
    this._overlayRef?.dispose();
  }
}
